import { Copy, Plus, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, cx } from "../../../components/ui";
import { placeholders, renameTemplate, renderTemplate, sampleValues, templateUses, type Definition, type Issue } from "./model";

const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
const small = "h-8 rounded-lg border border-line bg-surface px-2 text-sm";
const ID = /[^A-Za-z0-9_]/g;
type Template = Definition["templates"][number];

const BLANK = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE properties SYSTEM "http://java.sun.com/dtd/properties.dtd"><properties></properties>`;

/**
 * Report templates (docs/phase3-plan.md §1): the ReportDocument ActionData, properties XML with {{prompt:x}} and
 * {{prompt:x|yyyymmdd}} placeholders, shown with a preview filled from sample prompt values.
 */
export function TemplatesEditor({ def, issues, onChange }: { def: Definition; issues: Issue[]; onChange: (d: Definition) => void }) {
  const [selected, setSelected] = useState<string | null>(def.templates[0]?.id ?? null);
  const t = def.templates.find((x) => x.id === selected);
  const add = (from?: Template) => {
    let n = def.templates.length + 1;
    const base = from ? `${from.id}Copy` : "newReport";
    while (def.templates.some((x) => x.id === `${base}${n}`)) n++;
    const id = `${base}${n}`;
    onChange({
      ...def,
      templates: [
        ...def.templates,
        {
          id,
          data: from?.data ?? BLANK,
          source: from ? `copy of ${from.id}` : "Designer",
        },
      ],
    });
    setSelected(id);
  };
  return (
    <div className={cx("grid gap-4", t ? "lg:grid-cols-[18rem_minmax(0,1fr)]" : "")}>
      <div className="space-y-3">
        <div className="rounded-xl border border-line bg-surface divide-y divide-line">
          {def.templates.map((x) => {
            const bad = issues.some((i) => i.where === `template ${x.id}` && i.level === "error");
            const uses = templateUses(def, x.id).length;
            return (
              <button
                key={x.id}
                type="button"
                aria-pressed={selected === x.id}
                onClick={() => setSelected(x.id)}
                className={cx("w-full text-left px-3 py-2 hover:bg-surface-2", selected === x.id && "bg-accent-soft")}
              >
                <span className={cx("block font-mono text-sm truncate", bad ? "text-bad" : "text-ink")}>{x.id}</span>
                <span className="block text-xs text-muted truncate">
                  {rangeView(x.data) ?? "properties"} · {uses ? `used ${uses}×` : "not used"}
                </span>
              </button>
            );
          })}
          {!def.templates.length && <p className="px-3 py-2 text-sm text-muted">No templates yet.</p>}
        </div>
        <Button onClick={() => add()}>
          <Plus className="size-4" aria-hidden /> Add a template
        </Button>
      </div>
      {t && (
        <TemplatePanel
          key={t.id}
          def={def}
          t={t}
          issues={issues.filter((i) => i.where === `template ${t.id}`)}
          onChange={(next) =>
            onChange({
              ...def,
              templates: def.templates.map((x) => (x.id === t.id ? next : x)),
            })
          }
          onRename={(to) => {
            onChange(renameTemplate(def, t.id, to));
            setSelected(to);
          }}
          onCopy={() => add(t)}
          onDelete={() => {
            onChange({
              ...def,
              templates: def.templates.filter((x) => x.id !== t.id),
            });
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

/** The business class a ReportDocument template ranges over (RangeViewKeyName, e.g. EmployeePayment.SymbolicKey). */
const rangeView = (xml: string) => /<entry key="RangeViewKeyName">([^<.]+)/.exec(xml)?.[1];

function TemplatePanel({
  def,
  t,
  issues,
  onChange,
  onRename,
  onCopy,
  onDelete,
  onClose,
}: {
  def: Definition;
  t: Template;
  issues: Issue[];
  onChange: (t: Template) => void;
  onRename: (to: string) => void;
  onCopy: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [id, setId] = useState(t.id);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [samples, setSamples] = useState<Record<string, string>>(() => sampleValues(def));
  const [insert, setInsert] = useState({
    code: def.prompts[0]?.code ?? "",
    yyyymmdd: false,
  });
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setId(t.id), [t.id]);
  const taken = id !== t.id && def.templates.some((x) => x.id === id);
  const commitId = () => {
    if (id && !taken && id !== t.id) onRename(id);
    else setId(t.id);
  };
  const used = placeholders(t.data);
  const uses = templateUses(def, t.id);
  const preview = useMemo(() => parse(t.data, renderTemplate(t.data, samples)), [t.data, samples]);

  const insertPlaceholder = () => {
    if (!insert.code) return;
    const text = `{{prompt:${insert.code}${insert.yyyymmdd ? "|yyyymmdd" : ""}}}`;
    const el = area.current;
    const at = el ? el.selectionStart : t.data.length;
    const end = el ? el.selectionEnd : at;
    onChange({ ...t, data: t.data.slice(0, at) + text + t.data.slice(end) });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + text.length, at + text.length);
    });
  };
  const pretty = () =>
    onChange({
      ...t,
      data: t.data.replace(/></g, ">\n<").replace(/<entry([^>]*)>\n<\/entry>/g, "<entry$1></entry>"),
    });

  return (
    <section className="rounded-xl border border-line bg-surface p-4 space-y-4 text-sm" aria-label={`Edit template ${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs text-muted">{t.source ? `From ${t.source}` : "Report template"}</div>
          <h2 className="font-display font-semibold text-ink text-lg font-mono">{t.id}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" title="Copy" aria-label="Copy template" onClick={onCopy}>
            <Copy className="size-4" />
          </Button>
          <Button variant="ghost" title="Delete" aria-label="Delete template" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="size-4" />
          </Button>
          <Button variant="ghost" title="Close" aria-label="Close editor" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {issues.map((i) => (
        <p key={i.message} className={cx("text-xs", i.level === "error" ? "text-bad" : "text-warn")}>
          {i.level === "error" ? "Fix: " : "⚠ "}
          {i.message}
        </p>
      ))}
      {confirmDelete && (
        <div className="rounded-lg border border-bad bg-bad-soft p-3 space-y-2">
          <p>{uses.length ? `${t.id} is used by ${uses.join(", ")}. Those will need fixing before you can save a version.` : `Remove ${t.id}?`}</p>
          <div className="flex gap-2">
            <Button variant="danger" onClick={onDelete}>
              Remove
            </Button>
            <Button onClick={() => setConfirmDelete(false)}>Keep it</Button>
          </div>
        </div>
      )}

      <label className="block">
        <span className="text-ink-2 font-medium">Id</span>
        <input
          className={cx(input, "font-mono", taken && "border-bad")}
          value={id}
          onChange={(e) => setId(e.target.value.replace(ID, ""))}
          onBlur={commitId}
          onKeyDown={(e) => e.key === "Enter" && commitId()}
        />
        <span className="text-xs text-muted">
          {taken ? <span className="text-bad">Another template has this id.</span> : uses.length ? `Used by ${uses.join(", ")}. Renaming updates them.` : "Not used by any step yet."}
        </span>
      </label>

      <div className="space-y-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <span className="text-ink-2 font-medium">ActionData (properties XML)</span>
          <div className="flex flex-wrap items-center gap-2">
            <select className={small} value={insert.code} onChange={(e) => setInsert({ ...insert, code: e.target.value })} aria-label="Prompt to insert">
              {def.prompts.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={insert.yyyymmdd} onChange={(e) => setInsert({ ...insert, yyyymmdd: e.target.checked })} /> as YYYYMMDD
            </label>
            <Button onClick={insertPlaceholder}>Insert</Button>
            <Button variant="ghost" onClick={pretty} title="Put each tag on its own line">
              One tag per line
            </Button>
          </div>
        </div>
        <textarea
          ref={area}
          className="w-full min-h-64 rounded-lg border border-line bg-surface p-2 font-mono text-xs leading-relaxed"
          spellCheck={false}
          value={t.data}
          onChange={(e) => onChange({ ...t, data: e.target.value })}
          aria-label="Template XML"
        />
      </div>

      <div className="space-y-2 border-t border-line pt-4">
        <h3 className="text-sm font-semibold text-ink">Preview</h3>
        {used.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {used.map((code) => {
              const p = def.prompts.find((x) => x.code === code);
              return (
                <label key={code} className="text-xs">
                  <span className={cx("block", p ? "text-ink-2" : "text-bad")}>{p?.label ?? `${code} (no such prompt)`}</span>
                  <input className={small} type={p?.kind === "date" ? "date" : "text"} value={samples[code] ?? ""} onChange={(e) => setSamples({ ...samples, [code]: e.target.value })} />
                </label>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-muted">This template doesn't use any prompts, so every run sends the same report request.</p>
        )}
        {"error" in preview ? (
          <p className="text-bad text-xs">The filled template isn't well-formed XML: {preview.error}</p>
        ) : (
          <div className="rounded-lg border border-line overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="text-left text-muted border-b border-line sticky top-0 bg-surface">
                <tr>
                  <th className="px-2 py-1 font-medium">Key</th>
                  <th className="px-2 py-1 font-medium">Value as sent</th>
                </tr>
              </thead>
              <tbody>
                {preview.entries.map((e, i) => (
                  <tr key={i} className={cx("border-b border-line last:border-0 align-top", e.filled && "bg-accent-soft")}>
                    <td className="px-2 py-1 font-mono text-ink-2 whitespace-nowrap">{e.key}</td>
                    <td className="px-2 py-1 font-mono break-all">{e.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!("error" in preview) && <p className="text-xs text-muted">{preview.entries.length} entries. Highlighted rows are filled from prompts.</p>}
      </div>
    </section>
  );
}

/** The properties' entries after filling; an entry is marked when its raw value holds a placeholder. */
function parse(raw: string, xml: string): { entries: { key: string; value: string; filled: boolean }[] } | { error: string } {
  const read = (text: string): { key: string; value: string }[] | string => {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    const bad = doc.querySelector("parsererror");
    // Chrome wraps the message ("This page contains the following errors: error on line 1 at column …").
    if (bad) return (/error on line[^\n]*/.exec(bad.textContent ?? "")?.[0] ?? (bad.textContent ?? "").split("\n")[0]).slice(0, 200);
    return [...doc.querySelectorAll("entry")].map((e) => ({ key: e.getAttribute("key") ?? "", value: e.textContent ?? "" }));
  };
  const filled = read(xml);
  if (typeof filled === "string") return { error: filled };
  const before = read(raw);
  return { entries: filled.map((e, i) => ({ ...e, filled: typeof before !== "string" && /\{\{prompt:/.test(before[i]?.value ?? "") })) };
}
